import axios, {AxiosResponse} from 'axios'
import {ID} from '../../../../../../_metronic/helpers'
import {LovData, Data, QueryResponse, CampaignDetailResponse, CallStatusResponse, CallStatusFilter} from './_models'

const API_URL = process.env.REACT_APP_THEME_API_URL
const URL = `${API_URL}/call/campaign`
const URL_LOV = `${API_URL}/lov`
const GET_URL = `${API_URL}/call/campaign/query`

const getData = (query: string): Promise<QueryResponse> => {
  return axios.get(`${GET_URL}?${query}`).then((d: AxiosResponse<QueryResponse>) => d.data)
}

const getDataById = (id: string): Promise<CampaignDetailResponse> => {
  return axios.get(`${URL}/${id}`).then((res: AxiosResponse<CampaignDetailResponse>) => res.data)
}

const fetchData = (tableCodes: string[]): Promise<LovData> => {
  return axios
    .post(`${URL_LOV}/multidata`, { tableCodes })
    .then((res) => res.data)
}

const createData = (data: Data): Promise<Data | undefined> => {
  return axios
    .post(URL, data)
    .then((response: AxiosResponse<Data>) => response.data)
}

const updateData = (data: Data, id: string): Promise<Data | undefined> => {
  return axios
    .put(`${URL}/${id}`, data)
    .then((response: AxiosResponse<Data>) => response.data)
}

const deleteData = (dataId: string): Promise<void> => {
  return axios.delete(`${URL}/${dataId}`).then(() => {})
}

const deleteDataSubmission = (dataId: string): Promise<void> => {
  return axios.delete(`${URL}/delete-submission/${dataId}`).then(() => {})
}

const deleteMultipleDataSubmissions = (dataIds: Array<string>): Promise<any> => {
  return axios.post(`${URL}/delete-submissions`, { submissionIds: dataIds }).then(res => res.data)
}

const deleteSelectedData = (dataIds: Array<ID>): Promise<void> => {
  const requests = dataIds.map((id) => axios.delete(`${URL}/${id}`))
  return axios.all(requests).then(() => {})
}

const importLeads = (campaignId: string, leads: any[]): Promise<any> => {
  return axios
    .post(`${URL}/${campaignId}/import-leads`, leads)
    .then((response) => response.data)
}

const togglePredictiveStatus = (campaignId: string, active: boolean, delaySecs: number = 5): Promise<any> => {
  return axios
    .post(`${URL}/${campaignId}/toggle-predictive-status`, { active, delaySecs })
    .then((response) => response.data)
}

const resetPredictiveData = (campaignId: string): Promise<any> => {
  return axios
    .post(`${URL}/${campaignId}/reset-predictive-data`, {})
    .then((response) => response.data)
}

const getSubmissionsByCampaign = (campaignId: string, query: string, sortKey?: string, sortDir?: string): Promise<any> => {
  let url = `${URL}/${campaignId}/submissions?${query}`
  if (sortKey) {
    url += `&sort_key=${sortKey}&sort_dir=${sortDir || 'asc'}`
  }
  return axios
    .get(url)
    .then((res: AxiosResponse<any>) => res.data)
}

const bulkUpdateStatus = (submissionIds: string[], status: string): Promise<any> => {
  return axios.post(`${process.env.REACT_APP_THEME_API_URL}/call-campaigns/bulk-status`, { submissionIds, status }).then(res => res.data)
}

const bulkAssignAgent = (submissionIds: string[], agentId: string | null): Promise<any> => {
  return axios.post(`${process.env.REACT_APP_THEME_API_URL}/call-campaigns/bulk-assign`, { submissionIds, agentId }).then(res => res.data)
}

const getReport = (campaignId: string): Promise<any> => {
  return axios
    .get(`${URL}/${campaignId}/report`)
    .then((res: AxiosResponse<any>) => res.data)
}

const getPredictiveReport = (campaignId: string): Promise<any> => {
  return axios
    .get(`${URL}/${campaignId}/report/predictive`)
    .then((res: AxiosResponse<any>) => res.data)
}

const getPreviewReport = (campaignId: string): Promise<any> => {
  return axios
    .get(`${URL}/${campaignId}/report/preview`)
    .then((res: AxiosResponse<any>) => res.data)
}

const getHourlyReport = (campaignId: string, startDate?: string, endDate?: string): Promise<any> => {
  let query = '';
  if (startDate && endDate) {
    query = `?startDate=${startDate}&endDate=${endDate}`;
  } else if (startDate) {
    query = `?startDate=${startDate}`;
  } else if (endDate) {
    query = `?endDate=${endDate}`;
  }
  return axios
    .get(`${URL}/${campaignId}/report/hourly${query}`)
    .then((res: AxiosResponse<any>) => res.data)
}

const getDashboard = (campaignId: string, startDate?: string, endDate?: string): Promise<any> => {
  let query = '';
  if (startDate && endDate) {
    query = `?startDate=${startDate}&endDate=${endDate}`;
  }
  return axios
    .get(`${URL}/${campaignId}/dashboard-summary${query}`)
    .then((res: AxiosResponse<any>) => res.data)
}

const getAgent = async () => {
  return axios
    .get(`${API_URL}/roles/6/users`)
    .then((res) => res.data)
}

const getAssignedAgents = async (campaignId: string) => {
  return axios
    .get(`${URL}/${campaignId}/assigned-agents`)
    .then((res: AxiosResponse<any>) => res.data)
}

const assignAgent = (campaignId: string, agentId: string) => {
  return axios.post(`${URL}/${campaignId}/assign-agent`, { agentId })
}

const removeAgent = (campaignId: string, agentId: string) => {
  return axios.delete(`${URL}/${campaignId}/remove-agent/${agentId}`)
}

const updateAgentQuotas = (campaignId: string, payload: any[]) => {
  return axios.post(`${URL}/${campaignId}/update-quotas`, payload)
}

const saveOutboundCall = (payload: {
  campaignId: string,
  submissionId: string,
  welcomeStatusCall?: string,
  welcomeResultCall?: string,
  welcomeStatus?: string,
  productKnowledge?: string,
  notes: string,
  webhookResults: Record<string, string>
}) => {
  return axios.post(`${URL}/save-result`, payload)
}

const triggerCallInitiate = (campaignId: string, leadId:  string, fieldName: string) => {
  return axios.post(`${URL}/initiate-call`, {
    campaignId: campaignId,
    submissionId: leadId,
    fieldName: fieldName,
    timestamp: new Date().toISOString() 
  })
}

const getWelcomeStatusCall = async () => {
  return axios.get(`${API_URL}/lov/data/WSC_DATA`)
}

const getWelcomeResultCall = async () => {
  return axios.get(`${API_URL}/lov/data/WRC_DATA`)
}

const getWelcomeStatus = async () => {
  return axios.get(`${API_URL}/lov/data/WS_DATA`)
}
const getWelcomeProductKnowledge = async () => {
  return axios.get(`${API_URL}/lov/data/WPK_DATA`)
}

const getCampaignCallStatus = (filter: CallStatusFilter): Promise<CallStatusResponse> => {
  const params = new URLSearchParams()
  if (filter.agentId) params.append('agentId', filter.agentId)
  if (filter.dateFrom) params.append('dateFrom', filter.dateFrom)
  if (filter.dateTo) params.append('dateTo', filter.dateTo)
  if (filter.panggilanKe) params.append('panggilanKe', filter.panggilanKe.toString())
  if (filter.search) params.append('search', filter.search)
  if (filter.page) params.append('page', filter.page.toString())
  if (filter.items_per_page) params.append('items_per_page', filter.items_per_page.toString())
  if (filter.status) params.append('status', filter.status)
  
  return axios
    .get(`${URL}/${filter.campaignId}/call-status?${params.toString()}`)
    .then((res: AxiosResponse<CallStatusResponse>) => res.data)
}

const exportCallStatus = (campaignId: string, filter?: CallStatusFilter): Promise<Blob> => {
  const params = new URLSearchParams()
  if (filter?.agentId) params.append('agentId', filter.agentId)
  if (filter?.dateFrom) params.append('dateFrom', filter.dateFrom)
  if (filter?.dateTo) params.append('dateTo', filter.dateTo)
  if (filter?.panggilanKe) params.append('panggilanKe', filter.panggilanKe.toString())
  if (filter?.status) params.append('status', filter.status)
  if (filter?.search) params.append('search', filter.search)
  
  return axios
    .get(`${URL}/${campaignId}/call-status/export?${params.toString()}`, {
      responseType: 'blob',
      headers: { 'Authorization': `Bearer ${getApiToken()}` }
    })
    .then((res) => res.data)
}

const getApiToken = () => {
  try {
    const raw = localStorage.getItem('kt-auth-react-v')
    if (!raw) return ''
    const parsed = JSON.parse(raw)
    return parsed.api_token || ''
  } catch {
    return ''
  }
}

const updateLeadStatus = (id: string, statusSubmission: string): Promise<any> => {
  return axios
    .post(`${URL}/update-status/${id}`, { statusSubmission })
    .then((res: AxiosResponse<any>) => res.data)
}

const toggleAutoDial = (campaignId: string, active: boolean, delaySecs?: number): Promise<any> => {
  return axios
    .post(`${URL}/${campaignId}/toggle-auto-dial`, { active, delaySecs: delaySecs ?? 5 })
    .then((res) => res.data)
}

const toggleVoiceBlast = (campaignId: string, active: boolean): Promise<any> => {
  return axios
    .post(`${URL}/${campaignId}/toggle-voice-blast`, { active })
    .then((res) => res.data)
}

const getAutoDialStatus = (campaignId: string): Promise<any> => {
  return axios
    .get(`${URL}/${campaignId}/auto-dial-status`)
    .then((res) => res.data)
}

const testCall = (campaignId: string, timeoutSeconds: number): Promise<any> => {
  return axios
    .post(`${URL}/${campaignId}/test-call`, { timeoutSeconds })
    .then((res) => res.data)
}

const transferLeads = (campaignId: string, targetCampaignId: string, statusFilter?: string): Promise<any> => {
  return axios
    .post(`${URL}/${campaignId}/transfer-leads`, { targetCampaignId, statusFilter })
    .then((res) => res.data)
}

const resetVoiceBlastData = (campaignId: string): Promise<any> => {
  return axios
    .post(`${URL}/${campaignId}/reset-voiceblast-data`)
    .then((res) => res.data)
}

const getPredictiveMonitoringStats = (campaignId: string): Promise<any> => {
  return axios
    .get(`${URL}/${campaignId}/predictive-monitoring-stats`)
    .then((d: AxiosResponse<any>) => d.data)
}

export {
  getData, 
  deleteData, 
  deleteSelectedData,
  getDataById, 
  createData, 
  updateData, 
  fetchData,
  importLeads,
  getSubmissionsByCampaign,
  getAgent,
  getAssignedAgents,
  assignAgent,
  removeAgent,
  updateAgentQuotas,
  deleteDataSubmission,
  deleteMultipleDataSubmissions,
  saveOutboundCall,
  triggerCallInitiate,
  getReport,
  getPredictiveReport,
  getPreviewReport,
  getHourlyReport,
  getWelcomeStatusCall,
  getWelcomeResultCall,
  getWelcomeStatus,
  getWelcomeProductKnowledge,
  getDashboard,
  getCampaignCallStatus,
  exportCallStatus,
  updateLeadStatus,
  toggleAutoDial,
  toggleVoiceBlast,
  getAutoDialStatus,
  bulkUpdateStatus,
  bulkAssignAgent,
  togglePredictiveStatus,
  resetPredictiveData,
  resetVoiceBlastData,
  getPredictiveMonitoringStats,
  testCall,
  transferLeads
}